'use client';

import * as React from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { Button, type ButtonProps } from '@/components/ui/button';

interface MagneticButtonProps extends ButtonProps {
  strength?: number;
}

/**
 * Botón con efecto "magnético" que sigue al cursor del ratón.
 *
 * Importante: el efecto SOLO se aplica en dispositivos con puntero preciso
 * (ratón). En touch (móvil/tablet) lo desactivamos por completo porque
 * el `transform: translate()` puede desplazar el botón al hacer tap y
 * provocar que el primer toque falle.
 */
export const MagneticButton = React.forwardRef<
  HTMLButtonElement,
  MagneticButtonProps
>(({ children, strength = 22, className, ...props }, forwardedRef) => {
  const internalRef = React.useRef<HTMLDivElement | null>(null);
  const [hasFinePointer, setHasFinePointer] = React.useState(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 16, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 16, mass: 0.4 });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setHasFinePointer(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!hasFinePointer) return;
    const rect = internalRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / rect.width;
    const dy = (e.clientY - cy) / rect.height;
    x.set(dx * strength);
    y.set(dy * strength);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  if (!hasFinePointer) {
    return (
      <Button ref={forwardedRef} className={className} {...props}>
        {children}
      </Button>
    );
  }

  return (
    <motion.div
      ref={internalRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ x: sx, y: sy }}
      className="inline-block"
    >
      <Button ref={forwardedRef} className={className} {...props}>
        {children}
      </Button>
    </motion.div>
  );
});
MagneticButton.displayName = 'MagneticButton';
